import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { parseOutlookLinkRefusal } from './link-code-refusal'

/**
 * Link the signed-in Paxton account to the Microsoft account the code was issued to.
 * The API takes the Paxton account from the bearer and the Microsoft account from the stored code.
 */
export default async function postLinkCodeConsume(code: string): Promise<void> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error(kErrorCodeAuthError)

  const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/identity-bridge/link-codes/consume`
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json; charset=UTF-8' },
    body: JSON.stringify({ code }),
  })

  if (!response.ok) {
    const { data } = await readResponseJson(response)
    const refusal = parseOutlookLinkRefusal(response.status, data)
    if (refusal) throw refusal
    const error = new ApiError(response.status, 'The accounts could not be linked.')
    Sentry.captureException(error, { extra: getResponseDebugContext(response, apiUrl) })
    throw error
  }
  // Success is 204 with no body. Any other 2xx is not the contract and must not read as linked.
  if (response.status !== 204) {
    const error = new ApiResponsePayloadError(response.status, 'Unexpected link response status')
    Sentry.captureException(error, { extra: getResponseDebugContext(response, apiUrl) })
    throw error
  }
}
